import type { AppContext, AppPushNotificationSettings, BlockDefinition, PushPromptView } from "@softr-io/blocks-sdk";
import { useAppContext } from "@softr-io/blocks-sdk/react";
import { createContext, useContext, useMemo, useState } from "react";

/**
 * `register_service_worker` is set server-side by the platform and is `true` when either:
 *  - the PWA is enabled for the current device, or
 *  - push notifications are enabled for the current device.
 *
 * Both are per-device: the builder can offer the app on mobile only, desktop only, or both.
 * /pwa/sw.js backs the installed app and web push alike, so one worker serves either case and
 * the platform collapses the two into this single flag.
 *
 * It is `false` (or absent) when neither applies, and we must not register a worker then —
 * an app with the PWA and push both switched off should not be running one.
 */
export function shouldRegisterServiceWorker(appCtx: Pick<AppContext, "pwa_settings">) {
    return appCtx.pwa_settings?.register_service_worker === true;
}

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;
/**
 * Registers the service worker, calling this method repeatedly will return the same promise
 * with registration.
 */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!("serviceWorker" in navigator)) {
        return Promise.reject(new Error("Service workers not supported"));
    }
    if (!registrationPromise) {
        registrationPromise = navigator.serviceWorker.register("/pwa/sw.js", { scope: "/" });
    }
    return registrationPromise;
}

/**
 * Resolves with latest service worker registration once its worker is activated.
 *
 * We deliberately avoid `navigator.serviceWorker.ready`, which resolves with whichever
 * registration currently controls the page. Devices that installed the app before the native
 * PWA still carry a foreign worker registered at /progressier.js, and until the transition
 * worker served there unregisters it, that can be the one in control — push would then be
 * registered on a stale worker. Reading from our own `register()` promise guarantees push
 * lands on /pwa/sw.js.
 */
export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    if (!registrationPromise) {
        throw new Error("Service worker not registered; call registerServiceWorker() first");
    }
    const registration = await registrationPromise;
    await whenActivated(registration);
    return registration;
}

/** Resolves once the registration has an activated worker. */
function whenActivated(registration: ServiceWorkerRegistration): Promise<void> {
    if (registration.active?.state === "activated") return Promise.resolve();
    const worker = registration.installing ?? registration.waiting ?? registration.active;
    if (!worker) {
        return Promise.reject(new Error("Service worker registration has no worker"));
    }
    return new Promise<void>((resolve) => {
        worker.addEventListener("statechange", function onStateChange() {
            if (worker.state === "activated") {
                worker.removeEventListener("statechange", onStateChange);
                resolve();
            }
        });
    });
}

/**
 * We show a custom UI for install prompt async, so we want to intercept the event asap
 * to avoid a race condition.
 *
 * @see {https://github.com/khmyznikov/pwa-install#async-mode}
 */
export function interceptAndStoreBeforeInstallPromptEvent(): void {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        window.__softrBeforeInstallPromptEvent = e;
    });
}

declare global {
    interface Window {
        // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
        __softrBeforeInstallPromptEvent?: BeforeInstallPromptEvent;
    }
}

async function postSubscription(subscription: PushSubscription, opts?: { signal?: AbortSignal }): Promise<Response> {
    const json = subscription.toJSON();
    const res = await fetch("/v1/push/subscriptions", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            subscription: {
                endpoint: json.endpoint,
                keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
            },
            userAgent: navigator.userAgent,
        }),
        signal: opts?.signal,
    });
    if (!res.ok) {
        throw new Error("Subscription POST failed: " + res.status);
    }
    return res;
}

function getVapidPublicKey(settings: AppPushNotificationSettings | null | undefined): string | null {
    return settings?.vapid_public_key ?? null;
}

export async function subscribeToPush(
    settings: AppPushNotificationSettings | null | undefined,
    opts?: { signal?: AbortSignal },
): Promise<void> {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Push not supported on this browser");
    }
    const publicKey = getVapidPublicKey(settings);
    if (!publicKey) {
        throw new Error("Missing VAPID public key");
    }
    const publicKeyBytes = urlBase64ToUint8Array(publicKey);

    const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error("Notification permission " + permission);
    }

    // Use our own registration (not whatever controls the page) so push lands on /pwa/sw.js.
    const registration = await getServiceWorkerRegistration();
    if (opts?.signal?.aborted) return;
    const existing = await registration.pushManager.getSubscription();
    if (opts?.signal?.aborted) return;
    const subscription =
        existing ??
        (await registration.pushManager.subscribe({
            userVisibleOnly: true,
            // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
            applicationServerKey: publicKeyBytes,
        }));
    if (opts?.signal?.aborted) return;
    await postSubscription(subscription, opts);
}

/**
 * Ensures the user is subscribed to push notifications if they have already granted permission.
 * This is to cover two cases:
 *  - Permission was granted earlier, but we never pinged backend
 *  - Registration was lost (e.g. user cleared site data) but permission is still granted
 */
export async function ensurePushSubscriptionIfPermitted(
    settings: AppPushNotificationSettings | null | undefined,
    opts: { signal?: AbortSignal },
) {
    if (!supportsPushNotifications()) return;
    if (Notification.permission !== "granted") return;

    const registration = await getServiceWorkerRegistration();
    if (opts.signal?.aborted) return;
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
        const publicKey = getVapidPublicKey(settings);
        if (!publicKey) {
            throw new Error("Missing VAPID public key");
        }
        // If the existing subscription uses our current VAPID key, the backend already knows
        // about it and there is nothing to do. Otherwise it's a foreign subscription — signed
        // with another key, typically left behind on a device that subscribed before the native
        // PWA — which our backend cannot sign for, so drop it and resubscribe.
        if (subscriptionMatchesKey(existing, publicKey)) return;
        await existing.unsubscribe();
        if (opts.signal?.aborted) return;
    }

    await subscribeToPush(settings, opts);
}

function subscriptionMatchesKey(subscription: PushSubscription, publicKey: string): boolean {
    const existingKey = subscription.options.applicationServerKey;
    if (!existingKey) return false;
    const publicKeyBytes = urlBase64ToUint8Array(publicKey);
    const existingBytes = new Uint8Array(existingKey);
    return (
        existingBytes.length === publicKeyBytes.length && existingBytes.every((byte, i) => byte === publicKeyBytes[i])
    );
}

/** Convert a base64url VAPID public key into the byte array `subscribe()` needs. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
        output[i] = raw.charCodeAt(i);
    }
    return output;
}

export function isMobile(): boolean {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** True when push is enabled for the current device class in the workspace config. */
export function isPushEnabledForDevice(settings: AppPushNotificationSettings): boolean {
    return isMobile() ? !!settings.mobile_enabled : !!settings.desktop_enabled;
}

export function isUtilityPage(blocks: ReadonlyArray<BlockDefinition>): boolean {
    const utilityBlockNames = [
        "signIn",
        "signUp",
        "forgot-password",
        "reset-password",
        "account-settings",
        "onboarding",
    ];
    return blocks.some((def) => utilityBlockNames.includes(def.name));
}

const SKIP_KEY = "softrPwa.pushPromptSkipUntil";
export function isSkipActive(): boolean {
    try {
        const val = localStorage.getItem(SKIP_KEY);
        return val !== null && Date.now() < Number(val);
    } catch {
        return false;
    }
}

export function markSkipped(skipDays: number | null | undefined): void {
    try {
        const until = skipDays == null ? Number.MAX_SAFE_INTEGER : Date.now() + skipDays * 86_400_000;
        localStorage.setItem(SKIP_KEY, String(until));
    } catch {
        // ignore storage errors (private mode, etc.)
    }
}

export function supportsPushNotifications(): boolean {
    return "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
}

/**
 * Asks the service worker to reset its notification counter and clear the app icon badge.
 * No-op on browsers without service worker support.
 */
export async function clearAppBadge(): Promise<void> {
    if (!("serviceWorker" in navigator)) return;
    const registration = await getServiceWorkerRegistration();
    // We message the SW rather than calling `navigator.clearAppBadge()` directly so the SW's
    // persisted badge count is reset too; otherwise the next push would restore the stale count
    // from cache and re-inflate the badge.
    // The handler lives in the softr-platform codebase
    registration.active?.postMessage("reset-badge-count");
}

export function shouldShowPushPrompt(args: {
    settings: AppPushNotificationSettings | null | undefined;
    blocks: ReadonlyArray<BlockDefinition>;
    loggedIn: boolean;
}): boolean {
    const { settings, blocks, loggedIn } = args;
    const prompt = settings?.permission_prompt;

    if (!supportsPushNotifications()) {
        return false;
    }

    if (
        !loggedIn ||
        !settings ||
        !isPushEnabledForDevice(settings) ||
        isUtilityPage(blocks) ||
        isSkipActive() ||
        !prompt?.title
    ) {
        return false;
    }

    return Notification.permission === "default";
}

const HIDDEN_VIEW: PushPromptView = {
    visible: false,
    prompt: null,
    enable: async () => {},
    skip: () => {},
};

export const PushPromptContext = createContext<PushPromptView>(HIDDEN_VIEW);

export function usePushPromptState(blocks: ReadonlyArray<BlockDefinition>): PushPromptView {
    const appContext = useAppContext();
    const settings = appContext?.push_notification_settings;
    const [visible, setVisible] = useState(() => shouldShowPushPrompt({ settings, blocks, loggedIn: isLoggedIn() }));

    const prompt = useMemo(() => {
        const p = settings?.permission_prompt;
        if (!p) return null;
        return { title: p.title, message: p.message, enableButtonLabel: p.enable_button_label };
    }, [settings]);

    const skipDays = settings?.permission_prompt?.skip_days;

    return useMemo(
        () => ({
            visible,
            prompt,
            enable: async () => {
                try {
                    await subscribeToPush(settings);
                } catch {
                    // Permission denied or unsupported: apply the skip so we re-prompt later.
                    markSkipped(skipDays);
                } finally {
                    setVisible(false);
                }
            },
            skip: () => {
                setVisible(false);
                markSkipped(skipDays);
            },
        }),
        [visible, prompt, settings, skipDays],
    );
}

export function usePushPrompt(): PushPromptView {
    return useContext(PushPromptContext);
}

function isLoggedIn(): boolean {
    return !!window.logged_in_user;
}
